import { useState } from 'react'
import { ShieldCheck, ChevronDown, TriangleAlert } from 'lucide-react'
import type { NwsAlert } from '@/lib/nws/types'
import type { ThreatLevel } from '@/lib/criteria/levels'
import { alertLevel } from '@/lib/nws/status'
import { threatColor, threatLabel, levelRank } from '@/lib/criteria/levels'
import { fmtRelative } from '@/lib/time'

/**
 * The emotional center of the calm home. When nothing is active it reassures
 * ("All clear"); when alerts exist it surfaces them, most-severe first, with the
 * protective-action instruction one tap away.
 */
export function StatusBanner({
  level,
  alerts,
  locationLabel,
}: {
  level: ThreatLevel
  alerts: NwsAlert[]
  locationLabel: string
}) {
  if (alerts.length === 0) {
    return (
      <div className="status-calm card" style={{ '--c': threatColor('quiet') } as React.CSSProperties}>
        <div className="status-calm__ring">
          <ShieldCheck size={26} />
        </div>
        <div>
          <p className="status-calm__title">All clear</p>
          <p className="status-calm__sub">No active alerts for {locationLabel}</p>
        </div>
      </div>
    )
  }

  const urgent = levelRank(level) >= levelRank('severe')
  return (
    <div className={`alert-stack${urgent ? ' alert-stack--urgent' : ''}`}>
      <div className="alert-stack__head">
        <TriangleAlert size={16} style={{ color: threatColor(level) }} />
        <span className="label" style={{ color: threatColor(level) }}>
          {threatLabel(level)} · {alerts.length} active
        </span>
      </div>
      {alerts.map((a) => (
        <AlertCard key={a.id} alert={a} />
      ))}
    </div>
  )
}

function AlertCard({ alert }: { alert: NwsAlert }) {
  const [open, setOpen] = useState(false)
  const color = threatColor(alertLevel(alert))

  return (
    <article className="alert-card card" style={{ '--c': color } as React.CSSProperties}>
      <button className="alert-card__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="alert-card__bar" />
        <span className="alert-card__title">
          {alert.event}
          <span className="alert-card__meta">
            {alert.areaDesc.split(';')[0]}
            {alert.ends && ` · until ${fmtRelative(alert.ends).replace('in ', '')}`}
          </span>
        </span>
        <ChevronDown size={18} className={`alert-card__chev${open ? ' is-open' : ''}`} />
      </button>
      {open && (
        <div className="alert-card__body">
          {alert.headline && <p className="alert-card__headline">{alert.headline}</p>}
          {alert.instruction && (
            <div className="alert-card__instruction">
              <strong>What to do</strong>
              <p>{alert.instruction}</p>
            </div>
          )}
          <p className="alert-card__desc">{alert.description}</p>
          <p className="alert-card__sender">Issued by {alert.senderName}</p>
        </div>
      )}
    </article>
  )
}
